import listenerMiddleware from "App/listenerMiddleware";
import documentsActionTypes from "Areas/Documents/redux/actionTypes";
import documentsSidebarMenuActionTypes from "Areas/Sidebar/Documents/redux/actionTypes";
import createReducer from "Common/reducerUtils/createReducer";
import set from "Common/reducerUtils/set";
import isEqual from "lodash/isEqual";
import keyBy from "lodash/keyBy";
import omit from "lodash/omit";
import values from "lodash/values";
import { v4 as uuid } from "uuid";
import { NAMESPACE } from "../constants";
import actionCreators from "./actionCreators";
import actionTypes from "./actionTypes";
import reduxStoreExternal from "./reduxStoreExternal";
export const initialState = {
    [NAMESPACE]: {
        availableDocumentObjectIds: {},
        selectedDocumentObjectIds: {},
        sourceDataHeadersKey: {},
        sourceDataRowsKey: {},
        initialNodesSourceKey: {},
        nodesSourceKey: {},
        detailedPanelOpen: undefined,
        tableFilters: {},
        tableSortOptions: {}
    }
};
const setTableDataForDocument = (state, payload, documentId) => {
    reduxStoreExternal.sourceDataHeaders[documentId] = payload.Headers;
    let result = set(state, `[${NAMESPACE}].sourceDataHeadersKey[${documentId}]`, uuid());
    if (!isEqual(reduxStoreExternal.sourceDataRows[documentId], payload.Rows)) {
        reduxStoreExternal.sourceDataRows[documentId] = payload.Rows;
        result = set(result, `[${NAMESPACE}].sourceDataRowsKey[${documentId}]`, uuid());
    }
    reduxStoreExternal.initialNodesSource[documentId] = {};
    reduxStoreExternal.nodesSource[documentId] = {};
    result = set(result, `[${NAMESPACE}].initialNodesSourceKey[${documentId}]`, uuid());
    result = set(result, `[${NAMESPACE}].nodesSourceKey[${documentId}]`, uuid());
    if (payload.ObjectTypeArr)
        result = set(result, `[${NAMESPACE}].availableDocumentObjectIds[${documentId}]`, payload.ObjectTypeArr);
    result = set(result, `[${NAMESPACE}].tableFilters[${documentId}]`, undefined);
    return set(result, `[${NAMESPACE}].tableSortOptions[${documentId}]`, undefined);
};
export default createReducer(initialState, {
    [actionTypes.GET_AVAILABLE_DOCUMENT_OBJECT_IDS_COMPLETED]: (state, { meta, payload }) => {
        let result = set(state, `[${NAMESPACE}].availableDocumentObjectIds[${meta.documentId}]`, payload);
        if (!state[NAMESPACE].selectedDocumentObjectIds[meta.documentId])
            result = set(result, `[${NAMESPACE}].selectedDocumentObjectIds[${meta.documentId}]`, []);
        return result;
    },
    [actionTypes.SET_SELECTED_DOCUMENT_OBJECT_IDS]: (state, { documentId, documentObjectIds }) => set(state, `[${NAMESPACE}].selectedDocumentObjectIds[${documentId}]`, documentObjectIds),
    [actionTypes.GET_DOCUMENT_TABLE_DATA_COMPLETED]: (state, { payload, meta }) => (meta.saveToStore ? setTableDataForDocument(state, payload, meta.documentId) : state),
    [actionTypes.SAVE_DOCUMENT_TABLE_DATA_COMPLETED]: (state, { meta }) => {
        reduxStoreExternal.initialNodesSource[meta.documentId] = keyBy(meta.nodesSource, "NodeId");
        return set(state, `[${NAMESPACE}].initialNodesSourceKey[${meta.documentId}]`, uuid());
    },
    [actionTypes.GET_NODE_SOURCES_COMPLETED]: (state, { payload, meta }) => {
        const newNodeSources = keyBy(payload, "NodeId");
        reduxStoreExternal.initialNodesSource[meta.documentId] = {
            ...(reduxStoreExternal.initialNodesSource[meta.documentId] || {}),
            ...newNodeSources
        };
        const result = set(state, `[${NAMESPACE}].initialNodesSourceKey[${meta.documentId}]`, uuid());
        reduxStoreExternal.nodesSource[meta.documentId] = {
            ...(reduxStoreExternal.nodesSource[meta.documentId] || {}),
            ...newNodeSources
        };
        return set(result, `[${NAMESPACE}].nodesSourceKey[${meta.documentId}]`, uuid());
    },
    [actionTypes.CLEAR_DATA]: (state, { documentId }) => {
        reduxStoreExternal.sourceDataHeaders = omit(reduxStoreExternal.sourceDataHeaders, documentId);
        reduxStoreExternal.sourceDataRows = omit(reduxStoreExternal.sourceDataRows, documentId);
        reduxStoreExternal.nodesSource = omit(reduxStoreExternal.nodesSource, documentId);
        reduxStoreExternal.initialNodesSource = omit(reduxStoreExternal.initialNodesSource, documentId);
        return {
            ...state,
            [NAMESPACE]: {
                ...state[NAMESPACE],
                sourceDataHeadersKey: omit(state[NAMESPACE].sourceDataHeadersKey, documentId),
                sourceDataRowsKey: omit(state[NAMESPACE].sourceDataRowsKey, documentId),
                initialNodesSourceKey: omit(state[NAMESPACE].initialNodesSourceKey, documentId),
                nodesSourceKey: omit(state[NAMESPACE].nodesSourceKey, documentId),
                tableFilters: omit(state[NAMESPACE].tableFilters, documentId),
                tableSortOptions: omit(state[NAMESPACE].tableSortOptions, documentId),
                availableDocumentObjectIds: omit(state[NAMESPACE].availableDocumentObjectIds, documentId),
                selectedDocumentObjectIds: omit(state[NAMESPACE].selectedDocumentObjectIds, documentId)
            }
        };
    },
    [actionTypes.UPDATE_TABLE_DATA]: (state, { documentId, diff }) => {
        if (!reduxStoreExternal.sourceDataRows[documentId])
            return state;
        const updatedRows = {
            ...keyBy(reduxStoreExternal.sourceDataRows[documentId], "Id"),
            ...keyBy(diff, "Id")
        };
        reduxStoreExternal.sourceDataRows[documentId] = values(updatedRows);
        return set(state, `[${NAMESPACE}].sourceDataRowsKey[${documentId}]`, uuid());
    },
    [actionTypes.SET_NODE_SOURCES]: (state, { documentId, nodesSource, onlyDiffSources, updateInitialNodes }) => {
        if (!reduxStoreExternal.sourceDataRows[documentId] ||
            !reduxStoreExternal.nodesSource[documentId])
            return state;
        let result = state;
        const newNodesSource = keyBy(nodesSource, "NodeId");
        const documentNodesSourceMap = {
            ...(onlyDiffSources ? reduxStoreExternal.nodesSource[documentId] : {}),
            ...newNodesSource
        };
        reduxStoreExternal.nodesSource[documentId] = documentNodesSourceMap;
        result = set(result, `[${NAMESPACE}].nodesSourceKey[${documentId}]`, uuid());
        if (updateInitialNodes) {
            reduxStoreExternal.initialNodesSource[documentId] = documentNodesSourceMap;
            result = set(result, `[${NAMESPACE}].initialNodesSourceKey[${documentId}]`, uuid());
        }
        return result;
    },
    [actionTypes.SET_DETAILED_PANEL_OPEN]: (state, { detailedPanelOpen }) => set(state, `[${NAMESPACE}].detailedPanelOpen`, detailedPanelOpen),
    [actionTypes.SET_TABLE_FILTERS]: (state, { documentId, filtersJson }) => set(state, `[${NAMESPACE}].tableFilters[${documentId}]`, filtersJson),
    [actionTypes.SET_TABLE_SORT_OPTIONS]: (state, { documentId, dataField, sortOrder }) => set(state, `[${NAMESPACE}].tableSortOptions[${documentId}]`, { dataField, sortOrder })
});
listenerMiddleware.addListener({
    [documentsActionTypes.DELETE_DOCUMENT_COMPLETED]: ({ meta }, dispatch) => {
        dispatch(actionCreators.clearData(meta.documentId));
    },
    [documentsSidebarMenuActionTypes.SET_DOCUMENT_SELECTED]: ({ documentId, selected }, dispatch) => {
        if (documentId && !selected)
            dispatch(actionCreators.clearData(documentId));
    }
});
